import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { formatTag, slugifyTag } from "@/lib/tags";

type TagPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  published_at: string | null;
};
type TagGroup = { name: string; slug: string; count: number; posts: TagPost[] };

export const Route = createFileRoute("/blog/tags")({
  loader: async () => {
    const { data } = await supabase
      .from("posts")
      .select("id, slug, title, excerpt, published_at, tags")
      .eq("status", "published")
      .order("published_at", { ascending: false });
    const rows = (data as (TagPost & { tags: string[] | null })[]) ?? [];
    const map = new Map<string, TagGroup>();
    for (const row of rows) {
      for (const raw of row.tags ?? []) {
        const name = raw.trim();
        if (!name) continue;
        const key = name.toLowerCase();
        let g = map.get(key);
        if (!g) {
          g = { name: key, slug: slugifyTag(name), count: 0, posts: [] };
          map.set(key, g);
        }
        g.count += 1;
        g.posts.push({
          id: row.id,
          slug: row.slug,
          title: row.title,
          excerpt: row.excerpt,
          published_at: row.published_at,
        });
      }
    }
    const tags = [...map.values()].sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name),
    );
    return { tags };
  },
  head: () => {
    const url = "https://scottcbusiness.com/blog/tags";
    const title = "All tags | Field Notes";
    const description = "Browse every topic covered in Scott Cunningham's Field Notes.";
    return {
      meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://scottcbusiness.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Field Notes",
                item: "https://scottcbusiness.com/blog",
              },
              { "@type": "ListItem", position: 3, name: "Tags", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: TagsIndex,
});

function TagsIndex() {
  const { tags } = Route.useLoaderData() as { tags: TagGroup[] };
  return (
    <div className="blog-scope">
      <section className="relative overflow-hidden border-b border-border grain">
        <div
          className="blob float-slow -left-32 -top-24 h-[320px] w-[320px] bg-[var(--color-primary)]"
          style={{ opacity: 0.18 }}
        />
        <div
          className="blob float-slow right-[-120px] top-32 h-[300px] w-[300px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s", opacity: 0.18 }}
        />
        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-14 lg:px-10 lg:pt-24 lg:pb-20">
          <Breadcrumbs
            crumbs={[
              { label: "Home", to: "/" },
              { label: "Field Notes", to: "/blog" },
              { label: "Tags" },
            ]}
          />
          <h1 className="mt-5 text-[clamp(2rem,6vw,4.5rem)] leading-[0.95] tracking-tight text-foreground">
            All tags
          </h1>
          <p className="mt-4 max-w-2xl text-foreground/75">
            {tags.length} {tags.length === 1 ? "topic" : "topics"} across Field Notes. Jump to a
            tag to see every post filed under it.
          </p>
          {tags.length > 0 && (
            <ul className="mt-6 flex flex-wrap gap-2">
              {tags.map((t) => (
                <li key={t.slug}>
                  <a
                    href={`#${t.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-background/60 px-3 py-1 text-xs font-medium text-foreground/80 transition-colors hover:border-foreground hover:text-foreground"
                  >
                    {formatTag(t.name)}
                    <span className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px] text-foreground/70">
                      {t.count}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      <section className="min-h-[50vh] bg-foreground text-background">
        <div className="mx-auto max-w-5xl px-6 py-16 lg:py-20">
          {tags.length === 0 ? (
            <p className="text-white/70">No tags yet.</p>
          ) : (
            <div className="space-y-16">
              {tags.map((t) => (
                <section key={t.slug} id={t.slug} className="scroll-mt-24">
                  <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/15 pb-3">
                    <h2 className="font-display text-2xl tracking-tight text-white lg:text-3xl">
                      {formatTag(t.name)}
                    </h2>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                      {t.count} {t.count === 1 ? "post" : "posts"}
                    </span>
                  </div>
                  <ul className="mt-6 grid gap-6 md:grid-cols-2">
                    {t.posts.map((p) => (
                      <li key={p.id}>
                        <Link
                          to="/blog/$slug"
                          params={{ slug: p.slug }}
                          preload="intent"
                          className="group block"
                        >
                          <h3 className="font-display text-lg text-white transition-colors group-hover:underline">
                            {p.title}
                          </h3>
                          {p.excerpt && (
                            <p className="mt-1 text-sm text-white/70 line-clamp-2">{p.excerpt}</p>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
